"use client";

import { useRouter, useSearchParams } from "next/navigation";
import type { Auth, UserCredential, User as FirebaseUser } from "firebase/auth";
import {
  useState,
  useCallback,
  type MouseEventHandler,
  type FormEventHandler,
  type ChangeEvent,
  useEffect,
  useRef,
} from "react";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../ui/tabs";

import { useAuth } from "../../lib/auth/context";
import { getRedirectPath } from "../../lib/auth/redirect";
import {
  signInWithGoogle as sharedSignInWithGoogle,
  signInWithEmail as sharedSignInWithEmail,
  signOut as sharedSignOut,
} from "../../lib/firebase/auth";

/**
 * Configuration for LoginForm component
 */
export interface LoginFormConfig {
  // Feature flags
  enableMagicLinkEmail?: boolean;
  enableMagicLinkSMS?: boolean;
  enableGroupJoin?: boolean;
  requireOrgContext?: boolean;

  // Customization
  title?: string;
  description?: string;
  defaultRedirectPath?: string;
  adminRedirectPath?: string;
  loginHint?: string; // For Google Sign-In

  // Auth instance
  auth: Auth;

  // Sign-in functions (if not provided, uses shared functions)
  signInWithGoogle?: (auth: Auth) => Promise<unknown>;
  signInWithEmail?: (
    auth: Auth,
    email: string,
    password: string,
  ) => Promise<unknown>;
  signOut?: (auth: Auth) => Promise<void>;

  // Magic link functions (required if enableMagicLinkEmail or enableMagicLinkSMS is true)
  sendMagicLinkEmail?: (email: string, groupCode?: string) => Promise<void>;
  checkMagicLinkInUrl?: () => boolean;
  signInWithMagicLink?: (email?: string, emailLink?: string) => Promise<unknown>;
  initializeRecaptcha?: (containerId?: string) => unknown;
  sendMagicLinkSMS?: (
    phoneNumber: string,
    groupCode?: string,
  ) => Promise<{ verificationId: string; confirmationResult: unknown }>;
  verifySMSCode?: (verificationId: string, code: string) => Promise<unknown>;
  cleanupRecaptcha?: () => void;

  // Group join functions (required if enableGroupJoin is true)
  getGroupByCode?: (params: {
    groupCode: string;
    orgUid: string;
  }) => Promise<
    | { success: true; group: { uid: string; name: string; code: string } }
    | { success: false; error: string }
  >;
  requestGroupJoin?: (params: {
    groupCode: string;
    orgUid: string;
  }) => Promise<
    | { success: true; groupName: string }
    | { success: false; error: string }
  >;

  // Login processing callback
  onProcessUserLogin?: (params: {
    uid: string;
    orgUid?: string;
  }) => Promise<{
    success: boolean;
    isFirstLogin?: boolean;
    isAdmin?: boolean;
    appliedRoles?: Record<string, boolean>;
    invitedBy?: string;
    error?: string;
  }>;

  // Optional dependencies (for when org context is not required)
  orgUid?: string | null;
  orgLoading?: boolean;
  orgError?: Error | null;
}

/**
 * Handle group join request after authentication
 */
async function handleGroupJoinRequest(
  groupCode: string | null,
  orgUid: string | null,
  config: LoginFormConfig,
): Promise<{
  hasJoinRequest: boolean;
  joinRequestSuccess: boolean;
  message: string;
}> {
  if (!groupCode || !config.enableGroupJoin) {
    return { hasJoinRequest: false, joinRequestSuccess: false, message: "" };
  }

  if (!orgUid || !config.requestGroupJoin) {
    return {
      hasJoinRequest: true,
      joinRequestSuccess: false,
      message: "Organization not available",
    };
  }

  try {
    const joinResult = await config.requestGroupJoin({ groupCode, orgUid });

    if (joinResult.success) {
      return {
        hasJoinRequest: true,
        joinRequestSuccess: true,
        message: `Your request to join ${joinResult.groupName || "the group"} is pending leader approval.`,
      };
    } else {
      return {
        hasJoinRequest: true,
        joinRequestSuccess: false,
        message: joinResult.error || "Failed to request group join",
      };
    }
  } catch (joinError) {
    console.error("Error requesting group join:", joinError);
    return {
      hasJoinRequest: true,
      joinRequestSuccess: false,
      message:
        joinError instanceof Error
          ? joinError.message
          : "Failed to request group join",
    };
  }
}

export default function LoginForm(config: LoginFormConfig) {
  const { user } = useAuth();
  const router = useRouter();
  const params = useSearchParams();
  const orgUid = config.orgUid ?? null;
  const orgLoading = config.orgLoading ?? false;
  const orgError = config.orgError ?? null;
  const redirectUrl = params.get("redirect");
  const joinGroupCode = params.get("join");
  const [isLoading, setIsLoading] = useState(false);
  const [joinGroupName, setJoinGroupName] = useState<string | null>(null);
  const [joinRequestStatus, setJoinRequestStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Determine which tabs to show
  const hasMagicLinkEmail = config.enableMagicLinkEmail ?? false;
  const hasMagicLinkSMS = config.enableMagicLinkSMS ?? false;
  const hasGroupJoin = config.enableGroupJoin ?? false;
  const requireOrg = config.requireOrgContext ?? true;

  // Determine default tab
  const defaultTab = hasMagicLinkEmail
    ? "magic-link-email"
    : hasMagicLinkSMS
      ? "magic-link-sms"
      : "google";

  // Tab state
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Email magic link state
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicLinkEmailSent, setMagicLinkEmailSent] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);

  // SMS magic link state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsVerificationId, setSmsVerificationId] = useState<string | null>(
    null,
  );
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);

  // Email/password form state (only for dev/test/e2e)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const isDevOrTestOrE2E =
    process.env.NODE_ENV === "development" ||
    process.env.NODE_ENV === "test" ||
    (typeof window !== "undefined" &&
      window.location.hostname === "e2e.ohc400.org");

  // Ref to prevent duplicate magic link processing
  const processingMagicLink = useRef(false);

  // State to track if we're forcing a logout due to invalid session
  const [isForcingLogout, setIsForcingLogout] = useState(false);

  // Get sign-in functions (use provided or fall back to shared)
  const signInWithGoogle = useCallback(async () => {
    const fn = config.signInWithGoogle || sharedSignInWithGoogle;
    return fn(config.auth, config.loginHint);
  }, [config]);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      const fn = config.signInWithEmail || sharedSignInWithEmail;
      return fn(config.auth, email, password);
    },
    [config],
  );

  const signOut = useCallback(async () => {
    const fn = config.signOut || sharedSignOut;
    return fn(config.auth);
  }, [config]);

  // Check for invalid session parameter and force logout
  useEffect(() => {
    const sessionInvalid = params.get("session") === "invalid";
    if (sessionInvalid && user && !isForcingLogout) {
      console.warn("[LoginForm] Invalid session detected, forcing logout");
      setIsForcingLogout(true);
      setIsLoading(true);
      signOut()
        .then(() => {
          window.location.href = "/login";
        })
        .catch((error) => {
          console.error("[LoginForm] Error during forced logout:", error);
          window.location.href = "/login";
        });
    }
  }, [params, user, isForcingLogout, signOut]);

  // Load group name if join parameter is present
  useEffect(() => {
    const loadGroupInfo = async () => {
      if (
        joinGroupCode &&
        orgUid &&
        !orgLoading &&
        hasGroupJoin &&
        config.getGroupByCode
      ) {
        try {
          const result = await config.getGroupByCode({
            groupCode: joinGroupCode,
            orgUid,
          });
          if (result.success && result.group) {
            setJoinGroupName(result.group.name);
          } else if (!result.success) {
            setJoinRequestStatus({
              success: false,
              message: result.error || `Group "${joinGroupCode}" not found`,
            });
          }
        } catch (error) {
          console.error("Error loading group info:", error);
          setJoinRequestStatus({
            success: false,
            message:
              error instanceof Error
                ? error.message
                : "Failed to load group information",
          });
        }
      }
    };

    loadGroupInfo();
  }, [joinGroupCode, orgUid, orgLoading, hasGroupJoin, config]);

  // Check for magic link in URL on mount
  useEffect(() => {
    const checkMagicLink = async () => {
      if (processingMagicLink.current || !config.checkMagicLinkInUrl) {
        return;
      }

      if (requireOrg && (orgLoading || !orgUid)) {
        return;
      }

      if (config.checkMagicLinkInUrl()) {
        processingMagicLink.current = true;
        try {
          const emailFromUrl = params.get("email");
          setIsLoading(true);
          setMagicLinkError(null);

          if (!config.signInWithMagicLink) {
            throw new Error("Magic link sign-in not configured");
          }

          const result = await config.signInWithMagicLink(
            emailFromUrl || undefined,
          );

          // Process the user login if callback provided
          if (result && config.onProcessUserLogin && orgUid) {
            try {
              const userResult = result as UserCredential | { user?: FirebaseUser };
              if (userResult.user) {
                const loginResult = await config.onProcessUserLogin({
                  uid: userResult.user.uid,
                  orgUid,
                });

                if (loginResult.success) {
                  // Refresh token if available
                  if (userResult.user && typeof userResult.user.getIdToken === "function") {
                    await userResult.user.getIdToken(true);
                    const freshToken = await userResult.user.getIdToken();
                    await fetch("/api/login", {
                      headers: { Authorization: `Bearer ${freshToken}` },
                    });
                  }

                  // Handle group join if enabled
                  if (hasGroupJoin) {
                    const joinResult = await handleGroupJoinRequest(
                      params.get("join"),
                      orgUid,
                      config,
                    );
                    if (joinResult.hasJoinRequest) {
                      setJoinRequestStatus({
                        success: joinResult.joinRequestSuccess,
                        message: joinResult.message,
                      });
                      if (!joinResult.joinRequestSuccess) {
                        setIsLoading(false);
                        return;
                      }
                    }
                  }

                  // Clear URL parameters
                  if (typeof window !== "undefined") {
                    const url = new URL(window.location.href);
                    url.searchParams.delete("oobCode");
                    url.searchParams.delete("mode");
                    url.searchParams.delete("apiKey");
                    window.history.replaceState({}, "", url.toString());
                  }

                  // Redirect
                  document.location = getRedirectPath(
                    redirectUrl,
                    loginResult.isAdmin,
                    config,
                  );
                }
              }
            } catch (processError) {
              console.error("Error processing user login:", processError);
            }
          } else {
            // No processing needed, just redirect
            document.location = getRedirectPath(redirectUrl, undefined, config);
          }
        } catch (error) {
          console.error("Error signing in with magic link", error);
          const errorMessage =
            error instanceof Error ? error.message : "Failed to sign in";
          if (errorMessage.includes("invalid-action-code")) {
            setTimeout(() => {
              if (user) {
                document.location = getRedirectPath(
                  redirectUrl,
                  undefined,
                  config,
                );
                return;
              }
              setMagicLinkError(
                "This sign-in link has expired or has already been used. Please request a new one.",
              );
              setIsLoading(false);
              processingMagicLink.current = false;
            }, 500);
          } else {
            setMagicLinkError(errorMessage);
            setIsLoading(false);
            processingMagicLink.current = false;
          }
        }
      }
    };

    if (!user && (!requireOrg || (orgUid && !orgLoading))) {
      checkMagicLink();
    }
  }, [
    params,
    orgUid,
    orgLoading,
    user,
    redirectUrl,
    joinRequestStatus,
    config,
    requireOrg,
    hasGroupJoin,
  ]);

  const handleSignIn = useCallback<MouseEventHandler>(
    async (event) => {
      event.preventDefault();

      if (requireOrg) {
        if (orgLoading) {
          console.log("Organization is still loading, please wait...");
          return;
        }

        if (orgError || !orgUid) {
          console.error("Organization not available:", orgError);
          return;
        }
      }

      setIsLoading(true);
      try {
        const result = await signInWithGoogle();

        // Process the user login if callback provided
        let isAdmin: boolean | undefined;
        if (result && config.onProcessUserLogin && orgUid) {
          try {
            const userResult = result as UserCredential | { user?: FirebaseUser };
            if (userResult.user) {
              const loginResult = await config.onProcessUserLogin({
                uid: userResult.user.uid,
                orgUid,
              });

              if (loginResult.success) {
                isAdmin = loginResult.isAdmin;
                // Refresh token if available
                if (userResult.user && typeof userResult.user.getIdToken === "function") {
                  await userResult.user.getIdToken(true);
                }
              }
            }
          } catch (processError) {
            console.error("Error processing user login:", processError);
          }
        }

        document.location = getRedirectPath(redirectUrl, isAdmin, config);
      } catch (error) {
        console.error("Sign in error:", error);
        setIsLoading(false);
      }
    },
    [orgUid, orgLoading, orgError, redirectUrl, signInWithGoogle, config, requireOrg],
  );

  const handleSignOut = useCallback<MouseEventHandler>(
    async (event) => {
      event.preventDefault();
      setIsLoading(true);
      try {
        await signOut();
        document.location.reload();
      } catch (error) {
        console.error("Sign out error:", error);
        setIsLoading(false);
      }
    },
    [signOut],
  );

  const handleEmailSignIn = useCallback<FormEventHandler>(
    async (event) => {
      event.preventDefault();

      if (requireOrg) {
        if (orgLoading) {
          console.log("Organization is still loading, please wait...");
          return;
        }

        if (orgError || !orgUid) {
          console.error("Organization not available:", orgError);
          return;
        }
      }

      setIsLoading(true);
      try {
        const result = await signInWithEmail(email, password);

        // Process the user login if callback provided
        let isAdmin: boolean | undefined;
        if (result && config.onProcessUserLogin && orgUid) {
          try {
            const userResult = result as UserCredential | { user?: FirebaseUser };
            if (userResult.user) {
              const loginResult = await config.onProcessUserLogin({
                uid: userResult.user.uid,
                orgUid,
              });

              if (loginResult.success) {
                isAdmin = loginResult.isAdmin;
                // Refresh token if available
                if (userResult.user && typeof userResult.user.getIdToken === "function") {
                  await userResult.user.getIdToken(true);
                }
              }
            }
          } catch (processError) {
            console.error("Error processing user login:", processError);
          }
        }

        document.location = getRedirectPath(redirectUrl, isAdmin, config);
      } catch (error) {
        console.error("Email sign in error:", error);
        setIsLoading(false);
      }
    },
    [
      orgUid,
      orgLoading,
      orgError,
      redirectUrl,
      email,
      password,
      signInWithEmail,
      config,
      requireOrg,
    ],
  );

  const goToDashboard = useCallback(
    () => router.push(config.defaultRedirectPath || "/dashboard"),
    [router, config],
  );

  // Magic link email handler
  const handleSendMagicLinkEmail = useCallback<FormEventHandler>(
    async (event) => {
      event.preventDefault();
      if (!config.sendMagicLinkEmail) return;

      setMagicLinkError(null);
      setIsLoading(true);

      try {
        const groupCodeToUse =
          joinGroupCode ||
          (typeof window !== "undefined"
            ? window.localStorage.getItem("groupCodeForJoin")
            : null);

        await config.sendMagicLinkEmail(
          magicLinkEmail,
          groupCodeToUse || undefined,
        );
        setMagicLinkEmailSent(true);
      } catch (error) {
        console.error("Error sending magic link email", error);
        setMagicLinkError(
          error instanceof Error
            ? error.message
            : "Failed to send magic link email",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [magicLinkEmail, joinGroupCode, config],
  );

  // SMS magic link handlers
  const handleSendSMSCode = useCallback<FormEventHandler>(
    async (event) => {
      event.preventDefault();
      if (!config.sendMagicLinkSMS || !config.initializeRecaptcha) return;

      setSmsError(null);
      setIsLoading(true);

      try {
        if (!smsCodeSent && config.initializeRecaptcha) {
          config.initializeRecaptcha();
        }

        const groupCodeToUse =
          joinGroupCode ||
          (typeof window !== "undefined"
            ? window.localStorage.getItem("groupCodeForJoin")
            : null);

        const result = await config.sendMagicLinkSMS(
          phoneNumber,
          groupCodeToUse || undefined,
        );
        setSmsVerificationId(result.verificationId);
        setSmsCodeSent(true);
      } catch (error) {
        console.error("Error sending SMS code", error);
        setSmsError(
          error instanceof Error ? error.message : "Failed to send SMS code",
        );
        if (config.cleanupRecaptcha) {
          config.cleanupRecaptcha();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [phoneNumber, smsCodeSent, joinGroupCode, config],
  );

  const handleVerifySMSCode = useCallback<FormEventHandler>(
    async (event) => {
      event.preventDefault();

      if (!smsVerificationId || !config.verifySMSCode) {
        setSmsError("Verification ID not found. Please request a new code.");
        return;
      }

      if (requireOrg) {
        if (orgLoading) {
          console.log("Organization is still loading, please wait...");
          return;
        }

        if (orgError || !orgUid) {
          console.error("Organization not available:", orgError);
          return;
        }
      }

      setSmsError(null);
      setIsLoading(true);

      try {
        const result = await config.verifySMSCode(smsVerificationId, smsCode);

        // Process the user login if callback provided
        if (result && config.onProcessUserLogin && orgUid) {
          try {
            const userResult = result as UserCredential | { user?: FirebaseUser };
            if (userResult.user) {
              const loginResult = await config.onProcessUserLogin({
                uid: userResult.user.uid,
                orgUid,
              });

              if (loginResult.success) {
                // Handle group join if enabled
                if (hasGroupJoin) {
                  const joinFromUrl =
                    params.get("join") ||
                    (typeof window !== "undefined"
                      ? window.localStorage.getItem("groupCodeForJoin")
                      : null);

                  if (joinFromUrl && typeof window !== "undefined") {
                    window.localStorage.removeItem("groupCodeForJoin");
                  }

                  const joinResult = await handleGroupJoinRequest(
                    joinFromUrl,
                    orgUid,
                    config,
                  );
                  if (joinResult.hasJoinRequest) {
                    setJoinRequestStatus({
                      success: joinResult.joinRequestSuccess,
                      message: joinResult.message,
                    });
                    if (!joinResult.joinRequestSuccess) {
                      setIsLoading(false);
                      return;
                    }
                  }
                }

                document.location = getRedirectPath(
                  redirectUrl,
                  loginResult.isAdmin,
                  config,
                );
              }
            }
          } catch (processError) {
            console.error("Error processing user login:", processError);
          }
        } else {
          document.location = getRedirectPath(redirectUrl, undefined, config);
        }
      } catch (error) {
        console.error("Error verifying SMS code", error);
        setSmsError(
          error instanceof Error ? error.message : "Failed to verify code",
        );
        setIsLoading(false);
      }
    },
    [
      smsVerificationId,
      smsCode,
      orgUid,
      orgLoading,
      orgError,
      redirectUrl,
      params,
      config,
      requireOrg,
      hasGroupJoin,
    ],
  );

  // Cleanup reCAPTCHA on unmount
  useEffect(() => {
    return () => {
      if (config.cleanupRecaptcha) {
        config.cleanupRecaptcha();
      }
    };
  }, [config]);

  // Count available tabs
  const tabCount =
    (hasMagicLinkEmail ? 1 : 0) +
    (hasMagicLinkSMS ? 1 : 0) +
    1 + // Google
    (isDevOrTestOrE2E ? 1 : 0); // Email/Password

  // Not signed in state
  if (!user || !user.uid) {
    return (
      <Card className="w-full max-w-md mx-auto relative">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {config.title || "Old Hickory Council"}
            </CardTitle>
            <CardDescription className="text-lg text-gray-600">
              {config.description || "Scout Units"}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Loading overlay when processing magic link */}
          {isLoading && processingMagicLink.current && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
              <div className="text-center space-y-4">
                <svg
                  className="animate-spin h-8 w-8 text-blue-600 mx-auto"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="text-sm font-medium text-gray-700">
                  Signing you in...
                </p>
                <p className="text-xs text-gray-500">
                  Please wait while we complete your sign-in
                </p>
              </div>
            </div>
          )}
          {joinRequestStatus && (
            <div
              className={`p-4 rounded-lg mb-4 ${
                joinRequestStatus.success
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <p
                className={`text-sm ${
                  joinRequestStatus.success ? "text-green-800" : "text-red-800"
                }`}
              >
                {joinRequestStatus.message}
              </p>
            </div>
          )}
          {joinGroupCode && !joinRequestStatus && hasGroupJoin && (
            <div className="p-4 rounded-lg mb-4 bg-blue-50 border border-blue-200">
              <p className="text-sm text-blue-800 font-medium">
                {joinGroupName
                  ? `Joining ${joinGroupName}`
                  : `Joining group: ${joinGroupCode}`}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                After signing in, your request will be sent for leader approval.
              </p>
            </div>
          )}
          <div className="text-center space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Welcome to the Scout Communications Portal
            </h3>
            {requireOrg && orgLoading ? (
              <p className="text-sm text-gray-600">
                Loading organization settings...
              </p>
            ) : requireOrg && orgError ? (
              <p className="text-sm text-red-600">
                Unable to load organization. Please refresh the page.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Please sign in to access the portal.
              </p>
            )}
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList
              className={`grid w-full grid-cols-${tabCount}`}
              style={{
                gridTemplateColumns: `repeat(${tabCount}, minmax(0, 1fr))`,
              }}
            >
              {hasMagicLinkEmail && (
                <TabsTrigger value="magic-link-email">Email</TabsTrigger>
              )}
              {hasMagicLinkSMS && (
                <TabsTrigger value="magic-link-sms">SMS</TabsTrigger>
              )}
              <TabsTrigger value="google">Google</TabsTrigger>
              {isDevOrTestOrE2E && (
                <TabsTrigger value="email-password">Password</TabsTrigger>
              )}
            </TabsList>

            {/* Email Magic Link Tab */}
            {hasMagicLinkEmail && (
              <TabsContent value="magic-link-email" className="space-y-4">
                {magicLinkEmailSent ? (
                  <div className="space-y-4 text-center">
                    <div className="rounded-lg bg-green-50 p-4 border border-green-200">
                      <p className="text-sm text-green-800 font-medium">
                        Check your email!
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        We&apos;ve sent a sign-in link to{" "}
                        <span className="font-medium">{magicLinkEmail}</span>
                      </p>
                      <p className="text-xs text-green-600 mt-2">
                        Click the link in the email to sign in. The link will
                        expire after a short time.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setMagicLinkEmailSent(false);
                        setMagicLinkEmail("");
                      }}
                      className="w-full"
                    >
                      Send Another Link
                    </Button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSendMagicLinkEmail}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="magic-link-email">Email Address</Label>
                      <Input
                        id="magic-link-email"
                        type="email"
                        value={magicLinkEmail}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setMagicLinkEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        required
                        disabled={isLoading}
                      />
                    </div>
                    {magicLinkError && (
                      <div className="rounded-lg bg-red-50 p-3 border border-red-200">
                        <p className="text-sm text-red-800">{magicLinkError}</p>
                      </div>
                    )}
                    <Button
                      type="submit"
                      disabled={isLoading || (requireOrg && orgLoading)}
                      className="w-full"
                    >
                      {isLoading ? "Sending..." : "Send Magic Link"}
                    </Button>
                  </form>
                )}
              </TabsContent>
            )}

            {/* SMS Magic Link Tab */}
            {hasMagicLinkSMS && (
              <TabsContent value="magic-link-sms" className="space-y-4">
                {!smsCodeSent ? (
                  <form onSubmit={handleSendSMSCode} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone-number">Phone Number</Label>
                      <Input
                        id="phone-number"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
                        placeholder="+1234567890"
                        required
                        disabled={isLoading}
                      />
                      <p className="text-xs text-gray-500">
                        Enter your phone number with country code (e.g., +1 for
                        US)
                      </p>
                    </div>
                    {smsError && (
                      <div className="rounded-lg bg-red-50 p-3 border border-red-200">
                        <p className="text-sm text-red-800">{smsError}</p>
                      </div>
                    )}
                    <div id="recaptcha-container"></div>
                    <Button
                      type="submit"
                      disabled={isLoading || (requireOrg && orgLoading)}
                      className="w-full"
                    >
                      {isLoading ? "Sending..." : "Send Verification Code"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifySMSCode} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sms-code">Verification Code</Label>
                      <Input
                        id="sms-code"
                        type="text"
                        value={smsCode}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setSmsCode(e.target.value.replace(/\D/g, ""))
                        }
                        placeholder="123456"
                        maxLength={6}
                        required
                        disabled={isLoading}
                      />
                      <p className="text-xs text-gray-500">
                        Enter the 6-digit code sent to {phoneNumber}
                      </p>
                    </div>
                    {smsError && (
                      <div className="rounded-lg bg-red-50 p-3 border border-red-200">
                        <p className="text-sm text-red-800">{smsError}</p>
                      </div>
                    )}
                    <div className="flex space-x-2">
                      <Button
                        type="submit"
                        disabled={isLoading || (requireOrg && orgLoading)}
                        className="flex-1"
                      >
                        {isLoading ? "Verifying..." : "Verify Code"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setSmsCodeSent(false);
                          setSmsCode("");
                          setSmsVerificationId(null);
                          setSmsError(null);
                          if (config.cleanupRecaptcha) {
                            config.cleanupRecaptcha();
                          }
                        }}
                        className="flex-1"
                      >
                        Back
                      </Button>
                    </div>
                  </form>
                )}
              </TabsContent>
            )}

            {/* Google Auth Tab */}
            <TabsContent value="google" className="space-y-4">
              <Button
                onClick={handleSignIn}
                disabled={isLoading || (requireOrg && orgLoading)}
                className="w-full h-12 text-base font-medium"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing in...
                  </>
                ) : requireOrg && orgLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign In with Google
                  </>
                )}
              </Button>
            </TabsContent>

            {/* Email/Password Tab (Dev/Test/E2E Only) */}
            {isDevOrTestOrE2E && (
              <TabsContent value="email-password" className="space-y-4">
                <form onSubmit={handleEmailSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                      placeholder="user@test.ohc400.org"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                      placeholder="testpassword123"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading || (requireOrg && orgLoading)}
                    className="w-full"
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>
            )}
          </Tabs>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Need help? Contact your unit leader for access.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show loading state if we're forcing a logout
  if (isForcingLogout) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-6 text-center">
          <p className="text-gray-600">Clearing invalid session...</p>
        </CardContent>
      </Card>
    );
  }

  // Authorized user state
  return (
    <Card className="w-full max-w-md mx-auto border-green-200 bg-green-50">
      <CardHeader className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div>
          <CardTitle className="text-xl font-bold text-green-900">
            Welcome Back!
          </CardTitle>
          <CardDescription className="text-green-700">
            Signed in as {user.email}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Button
            onClick={goToDashboard}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            Go to Dashboard
          </Button>
          <Button
            onClick={handleSignOut}
            disabled={isLoading}
            variant="outline"
            className="w-full border-green-300 text-green-700 hover:bg-green-100"
          >
            {isLoading ? "Signing out..." : "Sign Out"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

